import React, { useMemo, useState } from "react";
import type { House, WorkerJobLog } from "../../lib/types";
import { fetchJson } from "../../lib/api";
import { useI18n } from "../../i18n/i18n";
import type { Locale } from "../../../i18n/translations";
import { localeLabels } from "../../../i18n/translations";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { CreateHouseForm } from "../houses/create-house-form";
import { HouseCard } from "../houses/house-card";

export const SettingsView = ({
  houses,
  logs,
  onRefresh,
  onRefreshLogs,
  selectedHouseId,
}: {
  houses: House[];
  logs: WorkerJobLog[];
  onRefresh: () => void;
  onRefreshLogs?: () => void;
  selectedHouseId?: number | null;
}) => {
  const { locale, setLocale, t } = useI18n();
  const [isUpdatingLocale, setIsUpdatingLocale] = useState(false);
  const [localeError, setLocaleError] = useState<string | null>(null);
  const orderedHouses = useMemo(() => {
    if (selectedHouseId == null) {
      return houses;
    }
    const selectedHouse = houses.find((house) => house.id === selectedHouseId);
    if (!selectedHouse) {
      return houses;
    }
    return [selectedHouse, ...houses.filter((house) => house.id !== selectedHouseId)];
  }, [houses, selectedHouseId]);

  const updateLocale = async (nextLocale: Locale) => {
    if (isUpdatingLocale || nextLocale === locale) {
      return;
    }
    setLocaleError(null);
    setIsUpdatingLocale(true);
    try {
      const response = await fetchJson<{ locale: Locale }>("/api/profile", {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({ locale: nextLocale }),
      });
      setLocale(response.locale);
      window.location.reload();
    } catch {
      setLocaleError(t("settings.localeError", "Could not update language. Please try again."));
    } finally {
      setIsUpdatingLocale(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <CreateHouseForm
          title={t("settings.createHouse", "Create a new house")}
          onCreated={onRefresh}
        />
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <p className="text-sm font-medium text-ink" id="locale-label">
            {t("settings.languageLabel", "Choose language")}
          </p>
          <div role="radiogroup" aria-labelledby="locale-label" className="flex flex-wrap gap-2 sm:justify-end">
            {Object.entries(localeLabels).map(([value, label]) => {
              const option = value as Locale;
              const isSelected = option === locale;
              return (
                <Button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  disabled={isUpdatingLocale}
                  onClick={() => void updateLocale(option)}
                >
                  {label}
                </Button>
              );
            })}
          </div>
          {localeError ? (
            <p className="text-sm text-error" role="alert">
              {localeError}
            </p>
          ) : null}
        </div>
      </div>
      {houses.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.noHouses", "No houses yet")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted">
              {t("settings.noHousesDescription", "Create a house to get started.")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {orderedHouses.map((house) => (
            <HouseCard
              key={house.id}
              house={house}
              logs={logs}
              onRefresh={onRefresh}
              onRefreshLogs={onRefreshLogs}
            />
          ))}
        </div>
      )}
    </div>
  );
};
